import React, { useState, useEffect, useRef } from 'react';
import { Play, Square, Loader2, CheckCircle, AlertCircle, ArrowRight, Brain, Eye, ShieldAlert, FileDown, Mail, Check, HelpCircle, Activity, ExternalLink } from 'lucide-react';
import Page from './Page';

import {utils} from './utils/index'

const REACT_APP_API_HOST = utils.config.REACT_APP_API_HOST;
export default function AgentDashboard() {
  const [task, setTask] = useState('');
  const [status, setStatus] = useState('idle'); // idle, running, success, error
  const [statusMessage, setStatusMessage] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [streamId, setStreamId] = useState(null);
  const [steps, setSteps] = useState([]);
  const [scanResult, setScanResult] = useState(null);
  
  // States for tracking email actions mapped by filename to prevent multi-row loading glitches
  const [emailStatusMap, setEmailStatusMap] = useState({}); // { [fileName]: 'idle' | 'sending' | 'success' | 'error' }
  const [emailMessageMap, setEmailMessageMap] = useState({}); // { [fileName]: string }

  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [task]);

  useEffect(() => {
    if (status !== 'running' || !streamId) return;

    console.log("[Production Log] Initializing SSE connection", { streamId });
    const source = new EventSource(`${REACT_APP_API_HOST}/agent/stream?streamId=${streamId}`);

    source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.debug("[Debug Log] SSE data received raw:", data);

        if (data.status === "connected") {
          setSteps((prev) => [...prev, { type: 'system', text: 'מתחבר לזרם העדכונים של הסוכן...', title: 'חיבור למערכת' }]);
          return;
        }

        switch (data.event) {
          case 'agent-start':
            setSteps(prev => [...prev, { type: 'system', text: `מתחיל סריקה בכתובת: ${data.url}`, title: 'איתחול דפדפן' }]);
            setStatusMessage('הסוכן החל את מחזור הריצה...');
            break;

          case 'agent-loop-step':
            setSteps(prev => [...prev, { type: 'step', text: `מבצע איטרציה ${data.iteration} מתוך ${data.maxIterations}`, title: 'אופטימיזציית תהליך' }]);
            break;

          case 'agent-thought':
            setSteps(prev => [...prev, { type: 'thought', text: data.thought, title: 'הסוכן מחשב מסלול' }]);
            setStatusMessage(`איטרציה ${data.iteration}: הסוכן חושב...`);
            break;

          case 'agent-action':
            setSteps(prev => [...prev, { type: 'action', text: data.action, title: 'מבצע פעולה בדף' }]);
            break;

          case 'agent-warning':
            setSteps(prev => [...prev, { type: 'warning', text: data.message, title: 'התגברות על מכשול' }]);
            break;

          case 'agent-download':
            setSteps(prev => [...prev, { type: 'success', text: `הקובץ ${data.filename} הורד בהצלחה`, title: 'הורדת תוכן' }]);
            setScanResult({ url: data.url || task, downloadedFile: data.filename, driveLinks: [] });
            break;

          case 'agent-click-error':
            setSteps(prev => [...prev, { type: 'error', text: data.error, title: 'שגיאת ניווט מקומית' }]);
            break;

          case 'agent-success':
            console.log("[Production Log] Agent reached success event trigger", { streamId, message: data.message });

            const containsZeroReceipts = /0\s+(receipts|files|קבלות|קבצים)/i.test(data.message);

            if (containsZeroReceipts) {
              console.warn("[Production Log] Agent claimed success but downloaded 0 assets. Converting to error state.");
              setSteps(prev => [...prev, {
                type: 'error',
                text: 'הסוכן סרק את הדף אך לא הצליח למצוא או להוריד קבלות תקינות.',
                title: 'לא נמצאו קבלות'
              }]);
              setStatus('error');
              setAiResponse('');
              setStatusMessage('הסוכן סיים את הסריקה, אך לא נמצאו קבלות להורדה בקישור שסופק.');
            } else {
              setSteps(prev => [...prev, { type: 'success', text: data.message, title: 'המשימה הושלמה!' }]);
              setStatus('success');
              setAiResponse(data.message);
              setStatusMessage('הסוכן סיים את המשימה בהצלחה!');

              setScanResult(prev => ({
                url: prev?.url || task,
                downloadedFile: prev?.downloadedFile || '',
                driveLinks: data.driveLinks || []
              }));
            }
            source.close();
            break;

          case 'agent-end':
            setStatus(current => current === 'running' ? 'success' : current);
            source.close();
            break;

          case 'agent-fatal-error':
            console.error("[Production Log] Agent encountered fatal error", { error: data.error, streamId });
            setSteps(prev => [...prev, { type: 'error', text: data.error, title: 'שגיאה קריטית' }]);
            setStatus('error');
            setStatusMessage(`הסוכן נכשל: ${data.error}`);
            source.close();
            break;

          default:
            if (data.message) {
              setSteps(prev => [...prev, { type: 'system', text: data.message, title: 'עדכון מערכת' }]);
            }
            break;
        }
      } catch (err) {
        console.error("[Debug Log] Error parsing SSE data json:", err);
      }
    };

    source.onerror = (err) => {
      console.error("[Production Log] SSE EventSource global error connection failed", err);
      setStatus('error');
      setStatusMessage('התרחשה שגיאה בתקשורת ה-SSE עם השרת.');
      source.close();
    };

    return () => {
      source.close();
    };
  }, [status, streamId, task]);

  const getStepIcon = ({ type }) => {
    switch (type) {
      case 'thought': return <Brain className="w-4 h-4 text-amber-500" />;
      case 'action': return <Eye className="w-4 h-4 text-indigo-500" />;
      case 'warning': return <ShieldAlert className="w-4 h-4 text-rose-500" />;
      case 'success': return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'error': return <AlertCircle className="w-4 h-4 text-rose-600" />;
      default: return <Activity className="w-4 h-4 text-slate-400" />;
    }
  };

  const handleStart = async ({ event }) => {
    event.preventDefault();
    if (!task.trim() || status === 'running') return;

    console.log("[Production Log] User triggered agent start sequence.");
    setScanResult(null);
    setSteps([]);
    setEmailStatusMap({});
    setEmailMessageMap({});
    setStreamId(null);

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const matches = task.match(urlRegex);

    if (!matches || matches.length === 0) {
      console.warn("[Production Log] Submission rejected: missing valid URL target.");
      setStatus('error');
      setStatusMessage('לא נמצאה כתובת URL תקינה בטקסט שהזנת. נא לכלול קישור המתחיל ב-http:// או https://');
      return;
    }

    const extractedUrl = matches[0];
    setStatus('running');
    setStatusMessage('מכין סביבת הרצה ומנפיק מפתח שידור ייחודי...');

    try {
      const localAppToken = localStorage.getItem('token');
      console.debug("[Debug Log] Fetching session token allocation ID from server");

      const sessionResponse = await fetch(REACT_APP_API_HOST + '/api/agent/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localAppToken}`
        },
      });

      if (!sessionResponse.ok) throw new Error('Failed to generate agent session id');
      const sessionData = await sessionResponse.json();
      const activeStreamId = sessionData.streamId;

      setStreamId(activeStreamId);
      setStatusMessage(`מזהה קישור: ${extractedUrl} ... משגר משימה לסוכן...`);

      const response = await fetch(REACT_APP_API_HOST + '/api/scrape', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localAppToken}`
        },
        body: JSON.stringify({
          url: [...matches],
          streamId: activeStreamId
        }),
      });

      if (!response.ok) throw new Error('Server error during scrape initiation');
    } catch (error) {
      console.error('[Production Log] Failed to initialize agent loop sequence:', error);
      setStatus('error');
      setStatusMessage('לא ניתן היה לאתחל את המשימה מול השרת. אנא ודא שהשרת זמין.');
    }
  };

  const handleStop = () => {
    console.log("[Production Log] User aborted current running operation manually.");
    setStatus('idle');
    setStatusMessage('');
    setSteps([]);
    setStreamId(null);
  };

  /**
   * Requests file forwarding via server email distribution pipeline.
   * @param {Object} params - The target configuration parameters.
   * @param {string} params.fileName - The explicit name of the asset to dispatch.
   * @returns {Promise<void>} Resolves when the operational context lifecycle settles.
   */
  const handleSendEmail = async ({ fileName }) => {
    if (!fileName || emailStatusMap[fileName] === 'sending') return;

    console.log("[Production Log] Dispatching email distribution request", { file: fileName });
    
    setEmailStatusMap(prev => ({ ...prev, [fileName]: 'sending' }));
    setEmailMessageMap(prev => ({ ...prev, [fileName]: '' }));

    try {
      const localAppToken = localStorage.getItem('token');
      const response = await fetch(REACT_APP_API_HOST + '/api/send-file', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localAppToken}`
        },
        body: JSON.stringify({ fileName }),
      });

      if (!response.ok) throw new Error('Failed to send email');

      setEmailStatusMap(prev => ({ ...prev, [fileName]: 'success' }));
      setEmailMessageMap(prev => ({ ...prev, [fileName]: 'הקובץ נשלח למייל בהצלחה!' }));
    } catch (error) {
      console.error('[Production Log] Email sending sequence encountered failure:', error);
      setEmailStatusMap(prev => ({ ...prev, [fileName]: 'error' }));
      setEmailMessageMap(prev => ({ ...prev, [fileName]: 'שגיאה בשליחה.' }));
    }
  };

  const handleResetWorkspace = () => {
    setTask('');
    setStatus('idle');
    setStatusMessage('');
    setSteps([]);
    setScanResult(null);
    setEmailStatusMap({});
    setEmailMessageMap({});
    setStreamId(null);
  };

  return (
    <Page totalFilesCount={scanResult?.downloadedFile ? 1 : 0} title={'סוכן'}>
      <div className="w-full flex flex-col justify-start max-w-3xl mx-auto space-y-6 text-right" dir="rtl">

        {/* טופס ההפעלה הראשי */}
        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm space-y-6">
          <div className="space-y-2">
            <h2 className="text-xl font-bold text-slate-800">
              סוכן ה-AI שלנו מוריד לכם את הקבלות אוטומטית ⚡
            </h2>
            <p className="text-sm leading-relaxed text-slate-500">
             פשוט מדביקים קישור – הסוכן כבר יטפל בהורדה וישמור את הקבלה ב-Google Drive שלכם.
            </p>
          </div>

          <form onSubmit={(e) => handleStart({ event: e })} className="space-y-4">
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={task}
                onChange={(e) => setTask(e.target.value)}
                disabled={status === 'running'}
                placeholder="הדבק קישור (URL) או רשום הנחיה לסוכן... (לדוגמה: כנס לקישור והורד את החשבונית האחרונה של גוגל)"
                className="w-full px-5 py-4 bg-slate-50/50 border border-slate-200 rounded-2xl text-base text-slate-800 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 disabled:bg-slate-100/80 disabled:text-slate-500 transition-all duration-200 resize-none min-h-[120px] max-h-[400px] overflow-y-auto leading-relaxed"
              />
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-50 p-3 rounded-xl">
              <HelpCircle className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span>המערכת מבוססת בינה מלאכותית ועלולה לטעות. יש לוודא את קובץ ההורדה</span>
            </div>

            <div>
              {status !== 'running' ? (
                <button
                  type="submit"
                  disabled={!task.trim()}
                  className="w-full py-3.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400 text-white font-medium text-sm rounded-xl shadow-sm hover:shadow transition-all duration-150 active:scale-[0.99] flex items-center justify-center gap-2"
                >
                  <Play className="w-4 h-4 fill-current" />
                  חלץ קישור ושגר לסוכן
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleStop}
                  className="w-full py-3.5 bg-rose-600 hover:bg-rose-700 text-white font-medium text-sm rounded-xl shadow-sm transition-all duration-150 active:scale-[0.99] flex items-center justify-center gap-2"
                >
                  <Square className="w-4 h-4 fill-current" />
                  ביטול ועצירת פעולה
                </button>
              )}
            </div>
          </form>
        </div>

        {/* כרטיס תצוגת מצב דינמי מאוחד */}
        {status !== 'idle' && (
          <div className={`p-5 rounded-2xl border transition-all duration-300 ${status === 'running' ? 'bg-indigo-50/40 border-indigo-100 text-indigo-900' :
            status === 'success' ? 'bg-emerald-50/40 border-emerald-100 text-emerald-950' :
              'bg-rose-50/40 border-rose-100 text-rose-950'
            }`}>
            <div className="flex items-start gap-3.5">
              <div className="mt-0.5 flex-shrink-0">
                {status === 'running' && <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />}
                {status === 'success' && <CheckCircle className="w-5 h-5 text-emerald-600" />}
                {status === 'error' && <AlertCircle className="w-5 h-5 text-rose-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-sm">
                  {status === 'running' && 'הסוכן מעבד כעת את הכתובת...'}
                  {status === 'success' && 'הפעולה הסתיימה בהצלחה!'}
                  {status === 'error' && 'התהליך נעצר'}
                </h3>
                <p className="text-xs opacity-90 mt-1 break-words leading-relaxed">
                  {statusMessage}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* כרטיס תוצאות סופיות - מוצג רק בסיום מוצלח של התהליך */}
        {status === 'success' && scanResult && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4 animate-fade-in">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-50 rounded-xl flex-shrink-0">
                <FileDown className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">תוצאת ניתוח קבלה</h4>
                <p className="text-sm font-semibold text-slate-800 mt-0.5 break-all">
                  {aiResponse || scanResult.downloadedFile}
                </p>
              </div>
            </div>

            {/* רשימת קבצי גוגל דרייב עם פעולות בשורה */}
            {scanResult.driveLinks && scanResult.driveLinks.length > 0 && (
              <div className="pt-4 border-t border-slate-100 mt-2">
                <h5 className="text-xs font-bold text-slate-500 mb-2">קבצים שגובו בגוגל דרייב:</h5>
                <div className="space-y-2">
                  {scanResult.driveLinks.map((link) => {
                    if (!link.receiptUrl) return null;
                    
                    const currentFilename = link.fileName || scanResult.downloadedFile || 'receipt.pdf';
                    const currentEmailStatus = emailStatusMap[currentFilename] || 'idle';
                    const currentEmailMessage = emailMessageMap[currentFilename] || '';

                    return (
                      <div key={link.googleDriveFileId || link.receiptUrl} className="space-y-1.5">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-slate-50/70 p-3 rounded-xl border border-slate-100 text-xs text-slate-700 transition-all hover:bg-slate-50 gap-3">
                          <span className="font-medium truncate max-w-full sm:max-w-[50%] text-slate-800 order-1">
                            {link.fileName || 'קובץ קבלה שהורד'}
                          </span>
                          
                          {/* בלוק כפתורי פעולה בצד שמאל */}
                          <div className="flex items-center gap-3 sm:gap-4 justify-start sm:justify-end order-2 flex-wrap">
                            <a
                              href={link.receiptUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-indigo-600 font-bold hover:text-indigo-800 transition-colors py-1"
                            >
                              <span>לצפיה בגוגל דרייב</span>
                              <ExternalLink className="w-3 h-3" />
                            </a>

                            <div className="h-3 w-px bg-slate-200 hidden sm:block" />

                            <button
                              type="button"
                              onClick={() => handleSendEmail({ fileName: currentFilename })}
                              disabled={currentEmailStatus === 'sending' || currentEmailStatus === 'success'}
                              className={`px-2.5 py-1.5 rounded-lg font-semibold text-[11px] flex items-center gap-1.5 transition-all border ${
                                currentEmailStatus === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                                currentEmailStatus === 'error' ? 'bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100' :
                                'bg-white border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-800 shadow-2xs'
                              }`}
                            >
                              {currentEmailStatus === 'sending' ? (
                                <>
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  <span>שולח...</span>
                                </>
                              ) : currentEmailStatus === 'success' ? (
                                <>
                                  <Check className="w-3 h-3" />
                                  <span>נשלח!</span>
                                </>
                              ) : (
                                <>
                                  <Mail className="w-3 h-3" />
                                  <span>שלח למייל שלי</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* הצגת שגיאה ממוקדת מתחת לשורה הספציפית במידה ונכשלה */}
                        {currentEmailStatus === 'error' && currentEmailMessage && (
                          <div className="text-[10px] font-medium text-rose-600 flex items-center gap-1 px-2">
                            <AlertCircle className="w-3 h-3 flex-shrink-0" />
                            <span>{currentEmailMessage}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ציר זמן: תהליך החלטות של הסוכן */}
        {status !== 'idle' && steps.length > 0 && (
          <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-50">
              <span className={`w-2 h-2 rounded-full ${status === 'running' ? 'bg-indigo-600 animate-pulse' : 'bg-slate-400'}`} />
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                {status === 'running' ? 'תהליך החלטות של הסוכן בזמן אמת' : 'סיכום ריצת הסוכן'}
              </h3>
            </div>

            <div className="relative border-r border-slate-100 pr-5 mr-2 space-y-5">
              {steps.map((step, index) => (
                <div key={index} className="relative space-y-1">
                  <div className="absolute -right-[31px] top-0.5 h-6 w-6 rounded-full bg-white border border-slate-100 shadow-sm flex items-center justify-center">
                    {getStepIcon({ type: step.type })}
                  </div>

                  <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100/60">
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-white border border-slate-200 text-slate-600 shadow-2xs">
                      {step.title || 'עדכון'}
                    </span>
                    <p className="text-xs text-slate-700 leading-relaxed mt-2 font-medium">
                      {step.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* כפתור איפוס ומשימה חדשה */}
        {(status === 'success' || status === 'error') && (
          <button
            onClick={handleResetWorkspace}
            className="pt-2 mx-auto flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
          >
            <ArrowRight className="w-4 h-4 transform rotate-180" />
            <span>ניקוי והזנת טקסט חדש</span>
          </button>
        )}

      </div>
    </Page>
  );
}